/**
 * Мигающая полоса тревоги со звуком (31.07): «ПОЖАР — идите к выходу».
 *
 * Показывается при БОЕВОМ кадре (живая тревога владельца или подписанный
 * `?e=`); учения не мигают и не звучат — сигнал тревоги обязан означать
 * тревогу, иначе на него перестают реагировать.
 *
 * Обе назойливости отключаемы и помнятся: паника эвакуации не помогает, а
 * мигание опасно при светочувствительной эпилепсии — поэтому «Не мигать»
 * такая же полноправная кнопка, как «Без звука».
 *
 * Живая тревога v2 (24.09.2026): городской баннер (независим от кадра
 * площадки — город может тревожить без локальной ЧС), сообщение площадки по
 * виду тревоги, список этажей, статус связи с сервером и спокойный баннер
 * для самостоятельного включения (SOS «сам», web-B self-frame.ts) и для
 * тревоги, переданной соседом без сети (relay.ts, AC13).
 */
import { tr } from '@app/i18n'
import { useEffect, useState } from 'preact/hooks'
import {
  alarmMuted,
  blinkDisabled,
  retrySirenOnFirstGesture,
  setAlarmMuted,
  setBlinkDisabled,
  startSiren,
  stopSiren,
} from '../emergency/alarm-attention'
import type { CityAlertKind, VenueAlertKind } from '../emergency/alert-types'
import type { EmergencyFrame } from '../emergency/frame'
import {
  alertCheckedAt,
  alertOffline,
  cityAlert,
  emergencyDrill,
  emergencyFrame,
  langTick,
  liveAlert,
  liveHazardUnits,
  venue,
} from '../state'
import { levelName } from './hazard-labels'

const VENUE_MSG_KEY: Readonly<Record<VenueAlertKind, Parameters<typeof tr>[0]>> = {
  fire: 'alarm.venue.fire',
  evac: 'alarm.venue.evac',
  flood: 'alarm.venue.flood',
  danger: 'alarm.venue.danger',
  shelter: 'alarm.venue.shelter',
}

const CITY_MSG_KEY: Readonly<Record<CityAlertKind, Parameters<typeof tr>[0]>> = {
  air: 'alarm.city.air',
  evac: 'alarm.city.evac',
}

/** «Проверено N мин назад» / «Нет связи» — честный статус опроса alert.json. */
function ConnectivityStatus() {
  const offline = alertOffline.value
  const checkedAt = alertCheckedAt.value
  if (offline) return <span class="alarm-status alarm-status--offline">{tr('alarm.offline')}</span>
  if (checkedAt === null) return null
  const minAgo = Math.floor((Date.now() - checkedAt) / 60000)
  return (
    <span class="alarm-status">
      {minAgo < 1 ? tr('alarm.checked_now') : tr('alarm.checked_ago', { min: String(minAgo) })}
    </span>
  )
}

/** Этажи под тревогой (liveAlert.levels) — по именам уровней RVF. */
function AlertedLevels() {
  const v = venue.value
  const levels = liveAlert.value?.levels ?? []
  if (!v || !levels.length) return null
  return (
    <span class="alarm-levels">
      {levels.map((id) => {
        const name = levelName(v, id)
        return name ? (
          <span class="alarm-level-chip" key={id}>
            {tr('alarm.levels', { name })}
          </span>
        ) : null
      })}
    </span>
  )
}

/** Городской баннер: независим от кадра площадки — своя тревога идёт и без него. */
function CityBanner() {
  const city = cityAlert.value
  if (!city?.active) return null
  return (
    <div class={`alarm-bar alarm-bar--city${city.drill ? ' alarm-bar--drill' : ''}`} role="alert">
      <span class="alarm-source">{tr('alarm.city_label')}</span>
      <span class="alarm-text">{tr(CITY_MSG_KEY[city.kind])}</span>
      {city.drill && <span class="alarm-drill-tag">{tr('alarm.drill_tag')}</span>}
      <ConnectivityStatus />
    </div>
  )
}

/**
 * Самостоятельное включение (SOS «сам», web-B) и тревога, переданная соседом
 * без сети (relay.ts), — ни сирены, ни мигания: подлинность не проверена,
 * это спокойная пометка, а не официальный сигнал.
 */
function calmKeyOf(frame: EmergencyFrame | null): 'self' | 'relay' | null {
  const key = frame?.keyId
  return key === 'self' || key === 'relay' ? key : null
}

function CalmBar({ keyId }: { keyId: 'self' | 'relay' }) {
  return (
    <div class="alarm-stack">
      <div class="alarm-bar alarm-bar--self" role="status">
        <span class="alarm-text">
          {tr(keyId === 'relay' ? 'alarm.relay_title' : 'alarm.self_title')}
        </span>
        {keyId === 'relay' && <ConnectivityStatus />}
      </div>
      <CityBanner />
    </div>
  )
}

export function AlarmBar() {
  langTick.value
  const frame = emergencyFrame.value
  const drill = emergencyDrill.value
  const live = liveAlert.value
  const [muted, setMuted] = useState(alarmMuted())
  const [still, setStill] = useState(blinkDisabled())
  const [needTap, setNeedTap] = useState(false)

  const calmKey = calmKeyOf(frame)
  const active = frame !== null && !drill && calmKey === null

  useEffect(() => {
    if (!active) {
      stopSiren()
      return
    }
    if (!startSiren()) {
      // Браузер держит звук до первого касания — честно просим тап.
      setNeedTap(true)
      retrySirenOnFirstGesture(() => setNeedTap(false))
    }
    return stopSiren
  }, [active])

  if (calmKey) return <CalmBar keyId={calmKey} />
  if (!active) {
    return cityAlert.value?.active ? (
      <div class="alarm-stack">
        <CityBanner />
      </div>
    ) : null
  }

  const burning = liveHazardUnits.value.length
  const venueMsgKey = live?.kind && live.kind !== 'drill' ? VENUE_MSG_KEY[live.kind] : null

  return (
    <div class="alarm-stack">
      <div class={`alarm-bar${still ? '' : ' alarm-blink'}`} role="alert">
        <span class="alarm-text">
          {tr('alarm.title')}
          {burning > 0 ? ` · ${tr('alarm.zones', { n: String(burning) })}` : ''}
          {venueMsgKey ? ` · ${tr(venueMsgKey)}` : ''}
        </span>
        <AlertedLevels />
        <ConnectivityStatus />
        <span class="alarm-actions">
          {needTap && !muted && (
            <button
              type="button"
              class="alarm-btn"
              onClick={() => startSiren() && setNeedTap(false)}
            >
              {tr('alarm.enable_sound')}
            </button>
          )}
          <button
            type="button"
            class="alarm-btn"
            aria-pressed={muted}
            onClick={() => {
              const next = !muted
              setAlarmMuted(next)
              setMuted(next)
              if (!next) startSiren()
            }}
          >
            {muted ? tr('alarm.sound_on') : tr('alarm.sound_off')}
          </button>
          <button
            type="button"
            class="alarm-btn"
            aria-pressed={still}
            onClick={() => {
              const next = !still
              setBlinkDisabled(next)
              setStill(next)
            }}
          >
            {still ? tr('alarm.blink_on') : tr('alarm.blink_off')}
          </button>
        </span>
      </div>
      <CityBanner />
    </div>
  )
}
